import { landmineNames } from '@mm/constants/landmine-names';
import audiencesCRUDActions from '@mm/state/actions/AudiencesCRUDActions';
import chatbotCRUDActions from '@mm/state/actions/ChatbotCRUDActions';
import pageCRUDActions from '@mm/state/actions/PageCRUDActions';
import pageGroupCRUDActions from '@mm/state/actions/PageGroupCRUDActions';
import triggerCRUDActions from '@mm/state/actions/TriggerCRUDActions';
import viewActions from '@mm/state/actions/viewActions';
import { BotCloneArgs } from '@mm/state/controllers/ChatbotController';
import { TemplatesController } from '@mm/state/controllers/TemplatesController';
import { TemplateModel } from '@mm/state/model/TemplateModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { selectAvailablePages } from '@mm/state/selectors/facebookPageSelector';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import {
  activeCompanySelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { Field, Form, Formik } from 'formik';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { compose } from 'recompose';
import { Dispatch } from 'redux';
import * as Yup from 'yup';
import Button from '../../../../../common/buttons/DeprecatedButton/DeprecatedButton';
import { PAGES_UPGRADE_COUNT } from '../../../../../common/constants/payments';
import { RouterComponentProps } from '../../../../../common/helpers/routes/interfaces';
import { timeout } from '../../../../../common/helpers/utils';
import {
  BOT_NAME_LENGTH_ERROR,
  MAX_BOT_NAME_LENGTH,
} from '../../../../../editor/features/configure/panels/sections/BotSettingsSection/BotName/BotName';
import { BotBuilderRoutes } from '../../../../../routes/bot-builder-routes';
import { TemplateFormLabel } from '../TemplateFormLabel/TemplateFormLabel';
import { TemplateModal } from '../TemplateModal/TemplateModal';
import { TrimInputSpaces } from '../TrimInputSpaces/TrimInputSpaces';
import { CreateOrExtendProgressBar } from './CreateOrExtendProgressBar';

const templateNameSchema = Yup.string()
  .trim()
  .max(MAX_BOT_NAME_LENGTH, BOT_NAME_LENGTH_ERROR)
  .required('Template Name Required');

export const validationSchema = Yup.object().shape({
  name: templateNameSchema,
});

type OuterProps = {
  searchToken: string;
  onClose: () => any;
  onOpenExtend?: () => any;
};

type InnerProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  RouterComponentProps;

type Props = OuterProps & InnerProps & RouteComponentProps;
type IsStillCloningType = (botId: number) => Promise<boolean | undefined>;
export const usePollTillClonedBot = (isStillCloning: IsStillCloningType) => {
  const memoizedPollTillClonedBot = useCallback(
    async (newBotId: number) => {
      const MAX_WAIT_TIME = 2 * 60 * 1000; // 2 minutes
      const POLL_INTERVAL = 3000;
      const pollMaxAttempts = MAX_WAIT_TIME / POLL_INTERVAL;
      let pollAttempts = 0;

      while (pollAttempts < pollMaxAttempts) {
        await timeout(POLL_INTERVAL);
        const stillCloning = await isStillCloning(newBotId);
        if (!stillCloning) return true;
        pollAttempts += 1;
      }

      throw new Error('Bot cloning timed out');
    },
    [isStillCloning],
  );
  return [memoizedPollTillClonedBot];
};

const CreateBotOrExtendByTemplate = ({
  searchToken,
  onClose,
  pageId,
  onOpenExtend,
  cloneBot,
  activeChatbot,
  isStillCloning,
}: Props) => {
  const company = useSelector(activeCompanySelector);
  const featureFlags = useSelector(combinedFeatureFlagsSelector);
  const availablePages = useSelector(selectAvailablePages);
  const [isProcessing, setIsProcessing] = useState(false);
  const [template, setTemplate] = useState({} as TemplateModel);
  const [isNewBot, setIsNewBot] = useState(false);
  const [newBotId, setNewBotId] = useState<number | undefined>(undefined);
  const [isCloning, setIsCloning] = useState(false);
  const modalActions = useModalActions();

  const onClickEndLink = useMemo(() => {
    const chatbotId = isNewBot && newBotId ? newBotId : activeChatbot?.id;

    if (chatbotId) {
      return BotBuilderRoutes.chatStarters.getLink({ chatbotId });
    }

    return '';
  }, [isNewBot, newBotId, activeChatbot]);

  const needsUpgrade =
    company?.editable &&
    availablePages?.data.length > PAGES_UPGRADE_COUNT &&
    !featureFlags.connectPageUnlimitedPages;

  const onEndClick = async () => {
    if (needsUpgrade) {
      modalActions.openUniversalUpgradeLandmineModal({
        landmineName:
          landmineNames['TEMPLATES__CREATE-OR-EXTEND__END__TAKE-ME-THERE'],
        feature: 'connectPageUnlimitedPages',
      });
    }
  };

  const getTemplateAsync = async (token: string) => {
    const t = await TemplatesController.verifyCodeExistance(token);
    if (t) {
      setTemplate(t);
    }
  };

  useEffect(() => {
    if (searchToken) {
      getTemplateAsync(searchToken);
    }
  }, [searchToken]);

  const [pollTillCloned] = usePollTillClonedBot(isStillCloning);

  const createFormSubmit = async (values: { name: string }) => {
    if (!featureFlags.botBuilderCreateFromTemplate) {
      modalActions.openUniversalUpgradeLandmineModal({
        landmineName:
          landmineNames['TEMPLATES__CREATE-OR-EXTEND__EXTEND-SUBMIT'],
        feature: 'botBuilderCreateFromTemplate',
      });
      onClose();
      return;
    }

    setIsProcessing(true);

    if (searchToken) {
      try {
        setIsCloning(true);
        const newBot = await cloneBot({
          facebookPageId: pageId,
          name: values.name,
          ref: searchToken,
          async: true,
        });
        if (newBot && newBot.id) {
          setNewBotId(newBot.id);
          await pollTillCloned(newBot.id);
        }
      } catch (err) {
        setIsCloning(false);
        throw err;
      }
    }
    setIsProcessing(false);
  };

  const extendFormSubmit = async (values: { name: string }) => {
    const { sample: isSample } = template;
    if (!featureFlags.botBuilderCreateFromTemplate && !isSample) {
      modalActions.openUniversalUpgradeLandmineModal({
        landmineName:
          landmineNames['TEMPLATES__CREATE-OR-EXTEND__EXTEND-SUBMIT'],
        feature: 'botBuilderCreateFromTemplate',
      });
      onClose();
      return;
    }
    setIsProcessing(true);
    if (activeChatbot) {
      try {
        setIsCloning(true);
        const newBot = await cloneBot({
          facebookPageId: pageId,
          targetBotId: activeChatbot.id,
          ref: searchToken,
          name: values.name,
          async: true,
        });
        if (newBot) {
          await pollTillCloned(newBot.id);
        }
      } catch (err) {
        setIsCloning(false);
      }
    }
    setIsProcessing(false);
  };

  const onRadioChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setIsNewBot(e.target.value === 'true');

  return (
    <TemplateModal isOpen={true}>
      <TemplateModal.Header toggle={onClose}>
        {!isCloning && <span>Import or create new?</span>}
      </TemplateModal.Header>
      {!isCloning ? (
        <TemplateModal.Body>
          <TemplateFormLabel title="Import into current Chatbot" flex>
            <input
              type="radio"
              name="type"
              value="false"
              defaultChecked
              onChange={onRadioChange}
            />
            <TemplateModal.SubHeader>
              Expand your Chatbot quickly by importing all contents of the{' '}
              <b>{template.name ? template.name : 'selected'}</b> template into{' '}
              <b>{activeChatbot ? activeChatbot.name : 'Current Chatbot'}</b>.{' '}
              None of your existing content will be overwritten.
            </TemplateModal.SubHeader>
          </TemplateFormLabel>
          <TemplateFormLabel title="Create new Chatbot" flex>
            <input
              type="radio"
              value="true"
              name="type"
              onChange={onRadioChange}
            />
            <TemplateModal.SubHeader>
              Use the <b>{template.name ? template.name : 'selected'}</b>{' '}
              template to create a new Chatbot from scratch. Keep in mind that
              only one Chatbot can be active on your Facebook page at a time{' '}
            </TemplateModal.SubHeader>
          </TemplateFormLabel>
          {isNewBot && (
            <Formik
              initialValues={{
                name: '',
              }}
              onSubmit={createFormSubmit}
              validationSchema={validationSchema}
            >
              <fieldset
                className="create-or-extend-bot-progress-bar__fieldset"
                disabled={isProcessing}
              >
                <Form>
                  <TrimInputSpaces>
                    <TemplateFormLabel>
                      <Field
                        name="name"
                        placeholder="Give your new bot a nice name"
                      />
                      <TemplateFormLabel.Error name="name" />
                    </TemplateFormLabel>
                  </TrimInputSpaces>
                  <TemplateModal.Footer>
                    {onOpenExtend ? (
                      <Button blue onClick={onOpenExtend}>
                        Import into Chatbot
                      </Button>
                    ) : (
                      <Button link onClick={onClose}>
                        Choose other template
                      </Button>
                    )}
                    <Button blue type="submit">
                      Create bot
                    </Button>
                  </TemplateModal.Footer>
                </Form>
              </fieldset>
            </Formik>
          )}
          {!isNewBot && (
            <Formik initialValues={{} as any} onSubmit={extendFormSubmit}>
              <fieldset disabled={isProcessing}>
                <Form>
                  <TemplateModal.Footer>
                    <Button link onClick={onClose}>
                      Choose other template
                    </Button>
                    <Button blue type="submit">
                      Add to Bot
                    </Button>
                  </TemplateModal.Footer>
                </Form>
              </fieldset>
            </Formik>
          )}
        </TemplateModal.Body>
      ) : (
        <TemplateModal.Body>
          <CreateOrExtendProgressBar
            isDone={!isProcessing}
            onClick={onEndClick}
            onClickEndLink={onClickEndLink}
          />
        </TemplateModal.Body>
      )}
    </TemplateModal>
  );
};

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  isStillCloning: async (botId: number) => {
    const maybeBot = await dispatch(chatbotCRUDActions.retrieve(botId, true));
    return maybeBot ? maybeBot.cloningProcess : true;
  },
  cloneBot: (botCloneArgs: BotCloneArgs) =>
    dispatch(chatbotCRUDActions.cloneBot(botCloneArgs)),
  setActiveChatbot: (chatbotId: number, userId: number) => {
    // Note: looks like we have 2+ sources of truth now in FE, setting both.
    dispatch(viewActions.activateChatbotEditor(chatbotId, userId));
    dispatch(chatbotCRUDActions.setFEActiveChatbot(chatbotId));
  },
  getAllBots: async (facebookPageId: number) =>
    dispatch(chatbotCRUDActions.retrieveAll(facebookPageId)),
  getBotPageGroups: async (chatbotId: number) =>
    dispatch(pageGroupCRUDActions.retrieveAll(chatbotId)),
  getDropDownPages: (chatbotId: number) =>
    dispatch(pageCRUDActions.retriveDropdownPages(chatbotId)),
  getBotPages: async (chatbotId: number) =>
    dispatch(pageCRUDActions.retrieveAll(chatbotId)),
  getAudiences: (chatbotId: number) =>
    dispatch(audiencesCRUDActions.retrieveAll(chatbotId)),
  getTriggers: (chatbotId: number) =>
    dispatch(triggerCRUDActions.retrieveAll({ botId: chatbotId })),
});

const mapStateToProps = (state: StoreState) => ({
  pageId: activeFacebookPageIdSelector(state),
  activeChatbot: state.chatbot.activeChatbot,
  session: state.session.session,
});

const enhance = compose<InnerProps, OuterProps>(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(CreateBotOrExtendByTemplate);
